<script lang="ts">
  import Table from '../../Table.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import Button from '../../Button.svelte';
  import DateRange from '../../DateRange.svelte';
  import { afterUpdate } from 'svelte';

  // Filter states
  let selectedStatus = '';
  let selectedEmployer = '';
  let startDate = '';
  let endDate = '';

  // Dropdown options
  const statusOptions = [
    { uuid: '', name: 'All Status' },
    { uuid: 'APPROVED', name: 'APPROVED' },
    { uuid: 'PENDING', name: 'PENDING' },
    { uuid: 'VERIFIED', name: 'VERIFIED' }
  ];

  const employerOptions = [
    // { uuid: '', name: 'Employer' },
    { uuid: 'techcorp', name: 'TechCorp' },
    { uuid: 'webify', name: 'Webify Ltd.' },
    { uuid: 'buildspace', name: 'BuildSpace' },
    { uuid: 'healthcare', name: 'HealthCare Inc' },
    { uuid: 'edugroup', name: 'EduGroup' }
  ];

  // Transaction data matching the screenshot
  const transactionsData = [
    {
      donorName: 'Emily T.',
      employer: 'TechCorp',
      hours: '08',
      monetaryValue: '$214.40',
      createdOn: 'May 2, 2024',
      status: 'APPROVED'
    },
    {
      donorName: 'John D.',
      employer: 'Webify Ltd.',
      hours: '12',
      monetaryValue: '$298.10',
      createdOn: 'April 30, 2024',
      status: 'PENDING'
    },
    {
      donorName: 'A. Khan',
      employer: 'BuildSpace',
      hours: '04',
      monetaryValue: '$102.80',
      createdOn: 'April 1, 2024',
      status: 'VERIFIED'
    }
  ];

  let filteredTransactions = transactionsData;

  // Table columns configuration
  const columns = [
    {
      key: 'donorName',
      label: 'Donor Name',
      width: '18%',
      render: (value: string) => `<span class="donor-name">${value}</span>`
    },
    {
      key: 'employer',
      label: 'Employer',
      width: '18%',
      render: (value: string) => `<span class="employer-name">${value}</span>`
    },
    {
      key: 'hours',
      label: 'Hours',
      width: '12%',
      render: (value: string) => `<span class="hours-value">${value}</span>`
    },
    {
      key: 'monetaryValue',
      label: 'Monetary Value',
      width: '16%',
      render: (value: string) => `<span class="monetary-value">${value}</span>`
    },
    {
      key: 'createdOn',
      label: 'Created On',
      width: '18%',
      render: (value: string) => `<span class="date-value">${value}</span>`
    },
    {
      key: 'status',
      label: 'Status',
      width: '22%',
      align: 'center',
      render: (value: string) => {
        const statusEnum = getStatusFromName(value);
        return `<div class="status-badge-wrapper" data-status="${statusEnum}"></div>`;
      }
    }
  ];

  function getStatusFromName(statusName: string): string {
    switch (statusName.toUpperCase()) {
      case 'APPROVED':
        return 'APPROVED';
      case 'REJECTED':
        return 'REJECTED';
      case 'COMPLETED':
        return 'COMPLETED';
      case 'VERIFIED':
        return 'VERIFIED'; // Using approved for verified (blue/green)
      default:
        return 'PENDING';
    }
  }

  // Add status badges to rendered table
  afterUpdate(() => {
    const statusWrappers = document.querySelectorAll('.status-badge-wrapper');
    statusWrappers.forEach((wrapper, index) => {
      if (!wrapper.hasChildNodes()) {
        const transaction = filteredTransactions[index % filteredTransactions.length];
        const statusClass = getStatusClass(transaction.status);
        const statusIcon = getStatusIcon(transaction.status);
        
        wrapper.innerHTML = `<div class="status-badge ${statusClass}">${statusIcon}<span>${transaction.status}</span></div>`;
      }
    });
  });

  function getStatusClass(status: string): string {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return 'approved';
      case 'REJECTED':
        return 'rejected';
      case 'COMPLETED':
        return 'completed';
      case 'VERIFIED':
        return 'verified';
      default:
        return 'pending';
    }
  }

  function getStatusIcon(status: string): string {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      case 'REJECTED':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>';
      case 'COMPLETED':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      case 'VERIFIED':
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
      default: // PENDING
        return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>';
    }
  }

  function handleApply() {
    // Implement filtering logic
    filteredTransactions = transactionsData.filter(transaction => {
      const matchesStatus = selectedStatus === '' || transaction.status === selectedStatus;
      const matchesEmployer = selectedEmployer === '' || 
        transaction.employer.toLowerCase().includes(selectedEmployer.toLowerCase());
      
      // Date filtering
      let matchesDateRange = true;
      if (startDate || endDate) {
        const transactionDate = new Date(transaction.createdOn);
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          matchesDateRange = transactionDate >= start && transactionDate <= end;
        } else if (startDate) {
          const start = new Date(startDate);
          matchesDateRange = transactionDate >= start;
        } else if (endDate) {
          const end = new Date(endDate);
          matchesDateRange = transactionDate <= end;
        }
      }
      
      return matchesStatus && matchesEmployer && matchesDateRange;
    });
  }

  function onStatusChange(value: string) {
    selectedStatus = value;
  }

  function onEmployerChange(value: string) {
    selectedEmployer = value;
  }
</script>

<div class="transaction-listing-container">
  <!-- Header -->
  <div class="page-header">
    <h1 class="page-title">Reports</h1>
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-left">
      <Dropdown
        options={statusOptions}
        selectedValue={selectedStatus}
        placeholder="All Status"
        onSelectionChange={onStatusChange}
      />
      
      <Dropdown
        options={employerOptions}
        selectedValue={selectedEmployer}
        placeholder="Employer"
        onSelectionChange={onEmployerChange}
      />
      
      <DateRange
        bind:startDate
        bind:endDate
        placeholder="Date Range"
      />
    </div>
    
    <div class="filters-right">
      <Button variant="primary" on:click={handleApply}>
        Apply
      </Button>
    </div>
  </div>

  <!-- Table Section -->
  <div class="table-section">
    <Table {columns} data={filteredTransactions} />
  </div>
</div>

<style>
  .transaction-listing-container {
    padding: 2rem;
    background-color: #f7fafc;
    min-height: 100vh;
  }

  .page-header {
    margin-bottom: 2rem;
  }

  .page-title {
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .filters-right {
    display: flex;
    align-items: center;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  /* Global styles for table content */
  :global(.donor-name) {
    font-weight: 500;
    color: #111827;
  }

  :global(.employer-name) {
    color: #6b7280;
  }

  :global(.hours-value) {
    color: #111827;
    font-weight: 500;
  }

  :global(.monetary-value) {
    font-weight: 600;
    color: #111827;
  }

  :global(.date-value) {
    color: #6b7280;
  }

  :global(.status-badge-wrapper) {
    display: flex;
    align-items: center;
  }

  :global(.status-badge-container) {
    display: flex;
    align-items: center;
  }

  /* Custom status badge styling for verified status */
  :global(.status-badge-container .status-badge.approved) {
    background-color: #10b981;
  }

  /* Status badge styles - matching donor request exactly */
  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
  }

  :global(.status-badge svg) {
    width: 16px;
    height: 16px;
    margin-right: 0.25rem;
    flex-shrink: 0;
    display: block;
  }

  :global(.status-badge span) {
    display: block;
    margin: 0;
    padding: 0;
  }

  :global(.status-badge.pending) {
    background-color: #FFF8E1;
    color: #F57C00;
  }

  :global(.status-badge.approved) {
    background-color: #E6F4EA;
    color: #34A853;
  }

  :global(.status-badge.rejected) {
    background-color: #FDEDEB;
    color: #EA4335;
  }

  :global(.status-badge.completed) {
    background-color: #E8F0FE;
    color: #007AFF;
  }

  :global(.status-badge.verified) {
    background-color: #DBEAFE;
    color: #3B82F6;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .filters-section {
      flex-direction: column;
      gap: 1rem;
    }

    .filters-left,
    .filters-right {
      width: 100%;
      justify-content: center;
    }
  }

  @media (max-width: 768px) {
    .transaction-listing-container {
      padding: 1rem;
    }

    .filters-left {
      flex-direction: column;
      width: 100%;
      gap: 0.75rem;
    }

    .filters-right {
      width: 100%;
      justify-content: center;
    }
  }
</style> 